import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDefaultCurrency } from '../../hooks/useDefaultCurrency';
import { useLocalCurrency } from '../../hooks/useLocalCurrency';
import { Check, ChevronsUpDown } from 'lucide-react';
import { cn } from '../../../lib/utils';
import { Button } from '../../../components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '../../../components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../../../components/ui/popover';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '../../../components/ui/tooltip';
import { Label } from '../../../components/ui/label';
import currenciesConfig from '../../../shared-config/currencies.config.json';
import countriesConfig from '../../../shared-config/countries.config.json';

// Type for currency data from config
interface CurrencyConfigData {
  name: string;
  symbol: string;
  primaryCountry: string;
  /** Alternate names people type for this currency, e.g. NIS for ILS. */
  aliases?: string[];
}

interface CountryConfigData {
  name: string;
  currency: string;
}

// Flag component using country-flag-icons CDN
function CurrencyFlag({ countryCode, size = 20 }: { countryCode: string; size?: number }) {
  // EU doesn't have a country flag, use a special EU flag
  const flagUrl = countryCode === 'EU'
    ? 'https://upload.wikimedia.org/wikipedia/commons/b/b7/Flag_of_Europe.svg'
    : `https://purecatamphetamine.github.io/country-flag-icons/3x2/${countryCode}.svg`;

  return (
    <img
      src={flagUrl}
      alt={countryCode}
      width={size * 1.5}
      height={size}
      style={{ objectFit: 'cover', borderRadius: 2 }}
      loading="lazy"
    />
  );
}

export interface CurrencyOption {
  code: string;
  name: string;
  symbol: string;
  primaryCountry: string;
  /** Everything the picker's search matches on. */
  searchTerms: string[];
}

interface CurrencyPickerProps {
  value: string | null;
  onChange: (code: string | null) => void;
  label?: string;
  placeholder?: string;
  helperText?: string;
  error?: boolean;
  errorMessage?: string;
  disabled?: boolean;
  required?: boolean;
  fullWidth?: boolean;
  size?: 'sm' | 'default';
  /** Show only these currencies (by code). If not provided, shows all. */
  allowedCurrencies?: string[];
  /** Compact mode: show only flag + code, no symbol (useful when symbol is shown elsewhere) */
  compact?: boolean;
  /**
   * Auto-select the viewer's default currency (user.preferred_currency >
   * tenant.default_currency > 'USD') when no value is set. On by default — a
   * currency field should open pre-filled. The default is re-applied if tenant
   * settings resolve after first render, but never overrides a value the user
   * picked or one the parent supplied.
   *
   * Pass `false` where an empty currency is meaningful rather than unfinished:
   * an optional override that inherits when blank, an "any currency" filter, or
   * an inline field on a detail page that saves on change (auto-filling there
   * would write to an existing record nobody touched).
   *
   * **If this picker can edit a record that already stores a currency, gate it
   * on that record:** `autoSelectDefaultCurrency={!entity?.currency_code}`. The
   * viewer default seeds an ABSENT currency; it must never override a stored
   * one. A parent that seeds its state in a `useEffect` renders this picker with
   * a stale `value === null` a commit before the seed lands, so an unguarded
   * picker silently relabels a EUR row to the viewer's currency at the same
   * nominal amount. The picker cannot detect that from the inside — `null` here
   * is indistinguishable from "the parent hasn't seeded yet" — which is why the
   * guard is the caller's and why `currencyPickerCoverage.test.ts` makes every
   * call site declare which case it is.
   * See docs/erp/modern-architecture-principles.md § P13 on edit.
   */
  autoSelectDefaultCurrency?: boolean;
  className?: string;
  /** Inline style for the container */
  style?: React.CSSProperties;
}

/** Pinned above the alphabetical list for every viewer — the de-facto global trade currency. */
const USD_CODE = 'USD';

// Every country that uses a given currency, so "israel" finds ILS and
// "germany" finds EUR — not just the one country whose flag we show.
const countriesByCurrency = Object.values(
  countriesConfig.countries as Record<string, CountryConfigData>
).reduce<Record<string, string[]>>((acc, country) => {
  (acc[country.currency] ??= []).push(country.name);
  return acc;
}, {});

// Build sorted currency options from config
const allCurrencyOptions: CurrencyOption[] = Object.entries(
  currenciesConfig.currencies as Record<string, CurrencyConfigData>
)
  .map(([code, data]) => ({
    code,
    name: data.name,
    symbol: data.symbol,
    primaryCountry: data.primaryCountry,
    // Everything a search should match: code, name, symbol, the countries that
    // use it, and the alternate names from config (NIS, RMB, …).
    searchTerms: [
      code,
      data.name,
      data.symbol,
      ...(data.aliases ?? []),
      ...(countriesByCurrency[code] ?? []),
    ],
  }))
  .sort((a, b) => a.code.localeCompare(b.code));

// Map for quick lookup
const currencyMap = new Map(allCurrencyOptions.map((c) => [c.code, c]));

export function CurrencyPicker({
  value,
  onChange,
  label,
  placeholder,
  helperText,
  error,
  errorMessage,
  disabled,
  required,
  fullWidth = true,
  size = 'default',
  allowedCurrencies,
  compact = false,
  autoSelectDefaultCurrency = true,
  className,
  style,
}: CurrencyPickerProps) {
  const { t } = useTranslation('common');
  const [open, setOpen] = useState(false);
  const resolvedPlaceholder = placeholder ?? t('pickers.selectCurrency');
  const defaultCurrency = useDefaultCurrency();
  const localCurrency = useLocalCurrency();

  // Keep the latest onChange in a ref so the auto-fill effect doesn't re-run (and
  // potentially double-fire) when a caller passes an inline arrow as onChange.
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  // Auto-fill the viewer's default currency when opted in. Tracking the value we
  // ourselves applied (vs. one the user picked) lets a late tenant-settings load
  // correct the initial 'USD' fallback without clobbering a real user choice.
  const userPickedRef = useRef(false);
  const appliedDefaultRef = useRef<string | null>(null);
  useEffect(() => {
    if (!autoSelectDefaultCurrency || userPickedRef.current) return;
    if (allowedCurrencies && !allowedCurrencies.includes(defaultCurrency)) return;
    if (value && value !== appliedDefaultRef.current) return; // parent-supplied value
    appliedDefaultRef.current = defaultCurrency;
    if (value !== defaultCurrency) onChangeRef.current(defaultCurrency);
  }, [autoSelectDefaultCurrency, value, defaultCurrency, allowedCurrencies]);

  const handleSelect = (code: string | null) => {
    userPickedRef.current = true;
    onChange(code);
  };

  const selectedOption = useMemo(
    () => (value ? currencyMap.get(value) || null : null),
    [value]
  );

  const currencyOptions = useMemo(() => {
    if (!allowedCurrencies) return allCurrencyOptions;
    return allCurrencyOptions.filter((c) => allowedCurrencies.includes(c.code));
  }, [allowedCurrencies]);

  // The currency the viewer transacts in, the US dollar, and the currency of the
  // country they sit in — the three codes people reach for — sit above the
  // alphabetical list rather than behind a scroll past AED/ARS. They are removed
  // from the long list, so every code appears exactly once.
  const { pinnedOptions, restOptions } = useMemo(() => {
    const pinnedCodes = [...new Set([defaultCurrency, USD_CODE, localCurrency].filter(
      (code): code is string => !!code
    ))];
    const pinned = pinnedCodes
      .map((code) => currencyOptions.find((c) => c.code === code))
      .filter((c): c is CurrencyOption => c !== undefined);
    const pinnedSet = new Set(pinned.map((c) => c.code));
    return {
      pinnedOptions: pinned,
      restOptions: currencyOptions.filter((c) => !pinnedSet.has(c.code)),
    };
  }, [currencyOptions, defaultCurrency, localCurrency]);

  const renderOption = (option: CurrencyOption) => (
    <CommandItem
      key={option.code}
      value={option.code}
      keywords={option.searchTerms}
      onSelect={() => {
        handleSelect(option.code);
        setOpen(false);
      }}
    >
      <Check
        className={cn(
          'me-2 h-4 w-4',
          value === option.code ? 'opacity-100' : 'opacity-0'
        )}
      />
      <CurrencyFlag countryCode={option.primaryCountry} size={16} />
      <span className="ms-2 font-medium">{option.symbol}</span>
      <span className="ms-1">{option.code}</span>
      <span className="ms-auto text-muted-foreground text-xs truncate">
        {option.name}
      </span>
    </CommandItem>
  );

  const buttonContent = selectedOption ? (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="flex items-center gap-1.5">
          <CurrencyFlag countryCode={selectedOption.primaryCountry} size={16} />
          {compact ? (
            <span className="font-medium whitespace-nowrap">{selectedOption.code}</span>
          ) : (
            <>
              <span className="font-medium">{selectedOption.symbol}</span>
              <span className="truncate">{selectedOption.code}</span>
            </>
          )}
        </span>
      </TooltipTrigger>
      <TooltipContent>
        {selectedOption.name}
      </TooltipContent>
    </Tooltip>
  ) : (
    resolvedPlaceholder
  );

  return (
    <div className={cn(fullWidth && 'w-full', className)} style={style}>
      {label && (
        <div className="mb-1.5 leading-none">
          <Label required={required}>
            {label}
          </Label>
        </div>
      )}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            disabled={disabled}
            className={cn(
              'justify-between font-normal',
              fullWidth && 'w-full',
              size === 'sm' && 'h-8 text-sm',
              !selectedOption && 'text-muted-foreground',
              error && 'border-destructive'
            )}
          >
            {buttonContent}
            <ChevronsUpDown className="ms-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[320px] p-0" align="start">
          <Command>
            <CommandInput placeholder={t('pickers.searchCurrency')} />
            <CommandList>
              <CommandEmpty>{t('pickers.noCurrencyFound')}</CommandEmpty>
              {pinnedOptions.length > 0 && (
                <>
                  <CommandGroup heading={t('pickers.suggestedCurrencies')}>
                    {pinnedOptions.map(renderOption)}
                  </CommandGroup>
                  <CommandSeparator />
                </>
              )}
              <CommandGroup heading={t('pickers.allCurrencies')}>
                {restOptions.map(renderOption)}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      {(errorMessage || helperText) && (
        <p className={cn(
          'text-xs mt-1',
          error || errorMessage ? 'text-destructive' : 'text-muted-foreground'
        )}>
          {errorMessage || helperText}
        </p>
      )}
    </div>
  );
}

// Export helper functions and data for external use
export function getCurrencyByCode(code: string): CurrencyOption | undefined {
  return currencyMap.get(code);
}

export function getCurrencySymbol(code: string): string {
  return currencyMap.get(code)?.symbol || code;
}

export function getCurrencyName(code: string): string {
  return currencyMap.get(code)?.name || code;
}

export function getAllCurrencies(): CurrencyOption[] {
  return allCurrencyOptions;
}

export { CurrencyFlag };
